// Owns what the page looks like once the visitor has been handed over to human support:
// the chat is put away and the support form section is revealed.
//
// Shared, because this happens from two places - straight after the hand-off on the chat page,
// and again on page load while a conversation is still waiting to be submitted, so that a
// reload reproduces the same state instead of showing the chat and the form at once.
/** Set by a section with "Hide until the chat asks for support" ticked. */
export const SUPPORT_SECTION_SELECTOR = '.js-chat-support-form';
const CHAT_FRAME_SELECTOR = '.chat-embed__frame';
const CHAT_EMBED_SELECTOR = '.js-docsbot-embed';
const BACK_CONTROL_CLASS = 'js-chat-back';
/** Matches the widget's own default, chosen for the sticky menu. */
const DEFAULT_SCROLL_OFFSET = 100;
/** Below this the visitor is still near the top, so scrolling them to the form is welcome. */
const SCROLL_TAKEOVER_LIMIT = 200;
/**
 * Finds the sections to reveal. Sections configured through the page builder carry the class;
 * an explicit id is the fallback for hand-built pages. More than one section can be marked, so
 * a form followed by, say, a contact block both come out of hiding.
 */
export function findSupportSections(targetId) {
    if (targetId) {
        const byId = document.getElementById(targetId);
        if (byId) {
            // An anchor reference renders as an empty <a> inside the section, so reveal the
            // section around it rather than the anchor itself.
            const resolved = byId.tagName === 'A' ? byId.closest('section') ?? byId : byId;
            return [resolved];
        }
    }
    return [...document.querySelectorAll(SUPPORT_SECTION_SELECTOR)];
}
function show(element) {
    element.hidden = false;
    element.classList.add('is-revealed');
    // The element may be hidden with an inline style, which would win over the attribute.
    if (element.style.display === 'none') {
        element.style.removeProperty('display');
    }
}
/**
 * Reveals the support form section(s), if there are any. Returns false when none exist - which is
 * normal on a page whose support form is visible anyway, so callers that require one report it.
 */
export function revealSupportSection(options = {}) {
    const targets = findSupportSections(options.targetId);
    if (targets.length === 0) {
        return false;
    }
    targets.forEach(show);
    const staysPut = options.respectVisitorScroll && window.scrollY > SCROLL_TAKEOVER_LIMIT;
    if (options.scroll !== false && !staysPut) {
        window.scrollTo({
            top: targets[0].getBoundingClientRect().top +
                window.scrollY -
                (options.scrollOffset ?? DEFAULT_SCROLL_OFFSET),
            behavior: 'smooth',
        });
    }
    return true;
}
function chatFrames() {
    return [...document.querySelectorAll(CHAT_EMBED_SELECTOR)].map((embed) => embed.closest(CHAT_FRAME_SELECTOR) ?? embed);
}
/**
 * Puts the embedded chat away. The visitor has been handed over, so leaving the chat next to
 * the support form would offer them two competing places to ask the same question.
 */
export function hideChatEmbed() {
    chatFrames().forEach((frame) => {
        frame.hidden = true;
    });
}
export function showChatEmbed() {
    chatFrames().forEach((frame) => {
        frame.hidden = false;
    });
}
/**
 * Adds a way back to the chat inside the revealed section.
 *
 * Without it a visitor who escalates and then changes their mind has no route back: the stored
 * conversation keeps the chat hidden until they submit the form or it expires. Only added where
 * there is a chat on the page to go back to, and only once.
 */
export function addBackToChatControl(onBack) {
    if (chatFrames().length === 0 || document.querySelector(`.${BACK_CONTROL_CLASS}`)) {
        return;
    }
    const [section] = findSupportSections();
    if (!section) {
        return;
    }
    const control = document.createElement('button');
    control.type = 'button';
    control.className = `chat-back ${BACK_CONTROL_CLASS}`;
    control.textContent = 'Back to the chatbot';
    control.addEventListener('click', onBack);
    hostFor(section).insertAdjacentElement('afterbegin', control);
}
/**
 * Picks where inside the section the control belongs. A section's own children sit outside its
 * width-constraining container, so putting the control there would leave it hanging off the edge of
 * the layout - it goes in the content column instead, lining up with the form.
 */
function hostFor(section) {
    return (section.querySelector('.grid-column') ??
        section.querySelector('.grid-container') ??
        section.querySelector(':scope > div') ??
        section);
}
/** Hides the support form section(s) again and removes the way-back control. */
export function hideSupportSection() {
    findSupportSections().forEach((section) => {
        section.hidden = true;
        section.classList.remove('is-revealed');
    });
    document.querySelectorAll(`.${BACK_CONTROL_CLASS}`).forEach((control) => control.remove());
}
/**
 * Undoes the hand-off on the page: form away, chat back, scrolled to it. Clearing the stored
 * conversation is the caller's job, so this stays free of storage concerns.
 */
export function returnToChat() {
    hideSupportSection();
    showChatEmbed();
    const [frame] = chatFrames();
    if (frame) {
        window.scrollTo({
            top: frame.getBoundingClientRect().top + window.scrollY - DEFAULT_SCROLL_OFFSET,
            behavior: 'smooth',
        });
    }
}
